// web/src/admin/adminShared.tsx — the few things all four console screens agree on
// (50_ADMIN_CONSOLE.md §3). Kept in one file so the window toggle, the percentage formatter and
// the margin tint thresholds cannot drift between Overview, Customers, Detail and Health.
import { useState } from 'react';
import type { AdminWindow } from '../lib/api';

export const WINDOWS: AdminWindow[] = [7, 30, 90];

/** 50 §8: "30 days by default, with 7/30/90 toggles." Session-local; not worth a preference. */
export function useAdminWindow(): [AdminWindow, (w: AdminWindow) => void] {
  const [days, setDays] = useState<AdminWindow>(30);
  return [days, setDays];
}

export function WindowToggle({ days, onChange }: { days: AdminWindow; onChange(w: AdminWindow): void }) {
  return (
    <div role="group" aria-label="Time window" className="inline-flex rounded-md border border-hair">
      {WINDOWS.map((w) => (
        <button
          key={w}
          onClick={() => onChange(w)}
          aria-pressed={days === w}
          className={`min-h-11 px-3 text-xs first:rounded-l-md last:rounded-r-md ${
            days === w ? 'bg-run text-runink' : 'text-muted hover:text-ink'
          }`}
        >
          {w}d
        </button>
      ))}
    </div>
  );
}

/**
 * A 0..1 ratio as a percentage — or an em dash.
 *
 * `null` means the denominator was zero, and it must NOT render as "0%". "0% rework" on a
 * platform that has not spent anything is a claim about quality; "—" is the truth. Every ratio in
 * the console DTOs is `number | null` for exactly this reason.
 */
export const pct = (v: number | null | undefined, digits = 0): string =>
  v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(digits)}%`;

/** Integer cents → dollars, to the CENT (50 §2: "money is rendered to the cent"). */
export const usd = (cents: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

/** Usage is rendered to the CREDIT (50 §2) — never converted to dollars on a screen. */
export const credits = (n: number): string => new Intl.NumberFormat('en-US').format(n);

/**
 * §3.2 row tint: amber under 40% margin, red under 20%.
 *
 * An org with no MRR (`marginPct === null`) gets NO tint. Tinting it red would put every trial
 * and every unbilled internal org at the top of the "about to cost me money" list and bury the
 * paying customer who is actually unprofitable — which is the one row the sort exists to surface.
 */
export function marginTint(marginPct: number | null): string {
  if (marginPct == null) return '';
  if (marginPct < 0.2) return 'bg-danger/10';
  if (marginPct < 0.4) return 'bg-warn/10';
  return '';
}
export function marginTone(marginPct: number | null): string {
  if (marginPct == null) return 'text-muted';
  if (marginPct < 0.2) return 'text-danger';
  if (marginPct < 0.4) return 'text-warn';
  return 'text-success';
}

/**
 * An org that pays nothing AND spends nothing. Margin is exactly $0 — numerically the smallest
 * number on the board — so a plain margin-ascending sort would put every trial and empty signup
 * above the paying customer who is genuinely unprofitable. Dormant rows sort LAST. The API's
 * ORDER BY does the same thing; both are here because the table can be re-sorted client-side.
 */
export const isDormant = (r: { mrrCents: number; aiSpendCents: number }): boolean =>
  r.mrrCents === 0 && r.aiSpendCents === 0;

/** Hours-blocked → a phrase KC can triage on at a glance (50 §3.4 / D-14). */
export function blockedFor(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}
